declare const Chart: any;
import AccountManager from './accountManager.js';
import d from './DOM.js';
import Toast from './toast.js';

/**
 * /report — a one-page A4 performance report (dark navy, neon green / cyan / purple) for an
 * account and date range, from the same /api/dashboard/metrics the dashboard uses. Opened from
 * Trade Data → "Export as… → PDF performance report". "Download PDF" rasterises the page with
 * html2canvas and wraps it in an A4 PDF with jsPDF (both vendored under /tv/vendor, since the
 * route CSP admits only same-origin scripts, and loaded on demand so they cost nothing unless
 * used); "Print" uses the browser's own print-to-PDF with a print stylesheet.
 */

const PAGE_W = 794;   // A4 @ 96 dpi
const NEON_GREEN = '#00f5a0';
const NEON_RED = '#ff3d71';
const GRID = 'rgba(0, 229, 255, 0.08)';
const TICK = '#8aa0c8';

const money = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
const num = (n: number, dp = 2) => new Intl.NumberFormat('en-US', { maximumFractionDigits: dp }).format(n);
const signPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

const loadScript = (src: string) => new Promise<void>((resolve, reject) => {
	if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
	const s = document.createElement('script');
	s.src = src;
	s.onload = () => resolve();
	s.onerror = () => reject(new Error(`Failed to load ${src}`));
	document.head.appendChild(s);
});

export default abstract class Report {
	private static params = { accountNumber: '', startDate: '', endDate: '' };

	public static async init() {
		const url = new URLSearchParams(window.location.search);
		Report.params = {
			accountNumber: url.get('accountNumber') || AccountManager.currentAccount?.accountNumber || '',
			startDate: url.get('startDate') || '',
			endDate: url.get('endDate') || '',
		};

		Report.fitToScreen();
		window.addEventListener('resize', Report.fitToScreen);
		d.qs<HTMLButtonElement>('#report-print').addEventListener('click', () => window.print());
		d.qs<HTMLButtonElement>('#report-download').addEventListener('click', () => Report.download());

		if (!Report.params.accountNumber) {
			d.qs<HTMLElement>('#report-toolbar-sub').textContent = 'No account selected.';
			return;
		}

		try {
			const q = new URLSearchParams({ accountNumber: Report.params.accountNumber });
			if (Report.params.startDate && Report.params.endDate) {
				q.set('startDate', Report.params.startDate);
				q.set('endDate', Report.params.endDate);
			}
			const res = await fetch(`/api/dashboard/metrics?${q.toString()}`);
			const data = await res.json();
			if (!res.ok || data.error) { throw new Error(data.error || `HTTP ${res.status}`); }
			Report.render(data);
			d.qs<HTMLButtonElement>('#report-print').disabled = false;
			d.qs<HTMLButtonElement>('#report-download').disabled = false;
		} catch (err) {
			console.error('Report failed', err);
			d.qs<HTMLElement>('#report-toolbar-sub').textContent = 'Could not load the report data.';
		}
	}

	/** Scale the fixed-size A4 page down to fit narrow screens (layout stays pixel-identical). */
	private static fitToScreen() {
		const wrap = d.qs<HTMLElement>('#report-scale');
		const page = d.qs<HTMLElement>('#report-page');
		if (!wrap || !page) { return; }
		const scale = Math.min(1, wrap.clientWidth / PAGE_W);
		page.style.transform = scale < 1 ? `scale(${scale})` : '';
		wrap.style.height = `${page.offsetHeight * scale}px`;
	}

	private static rangeLabel() {
		const { startDate, endDate } = Report.params;
		const fmt = (s: string) => {
			const [y, m, dd] = s.split('-').map(Number);
			return new Date(y, m - 1, dd).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
		};
		return startDate && endDate ? `${fmt(startDate)} – ${fmt(endDate)}` : 'All time';
	}

	private static render(data: any) {
		const { overall, risk, skill, insights, timeBased, equityCurve } = data;
		const account = AccountManager.accounts.find(a => a.accountNumber === Report.params.accountNumber);
		const accountLabel = account ? `${account.brokerName} · ${account.accountNumber}` : Report.params.accountNumber;

		d.qs<HTMLElement>('#report-toolbar-sub').textContent = `${accountLabel} · ${Report.rangeLabel()}`;
		d.qs<HTMLElement>('#rp-meta').innerHTML = `<span>ACCOUNT&nbsp; <b>${esc(accountLabel)}</b></span><span>PERIOD&nbsp; <b>${esc(Report.rangeLabel())}</b></span><span>TRADES&nbsp; <b>${overall.totalTrades}</b></span>`;
		d.qs<HTMLElement>('#rp-generated').textContent = `Generated ${new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })} · TradeVision`;

		// Total return (net profit on the balance base; same figure as the dashboard's % card)
		const total = d.qs<HTMLElement>('#rp-total-return');
		const equityStat = d.qs<HTMLElement>('#rp-equity-return');
		if (overall.netProfitPercent === null || overall.netProfitPercent === undefined) {
			total.textContent = '—';
			equityStat.textContent = '';
		} else {
			total.textContent = signPct(overall.netProfitPercent);
			equityStat.textContent = `Total Return: ${signPct(overall.netProfitPercent)}`;
		}
		const tone = overall.netProfit >= 0 ? NEON_GREEN : NEON_RED;
		total.style.color = tone;
		total.style.textShadow = `0 0 18px ${tone}88`;
		equityStat.style.color = tone;

		// Long vs short
		const ls = insights.longShort;
		const setPnl = (id: string, v: number) => { const el = d.qs<HTMLElement>(id); el.textContent = money(v); el.style.color = v >= 0 ? NEON_GREEN : NEON_RED; };
		setPnl('#rp-long', ls.long.pnl);
		setPnl('#rp-short', ls.short.pnl);
		const totalAbs = Math.abs(ls.long.pnl) + Math.abs(ls.short.pnl);
		d.qs<HTMLElement>('#rp-long-bar').style.width = `${totalAbs > 0 ? (Math.abs(ls.long.pnl) / totalAbs) * 100 : 50}%`;
		d.qs<HTMLElement>('#rp-short-bar').style.width = `${totalAbs > 0 ? (Math.abs(ls.short.pnl) / totalAbs) * 100 : 50}%`;

		// Stat tiles
		const duration = (ms: number) => {
			if (!ms) { return '0m'; }
			const minutes = Math.floor(ms / 60000);
			const hours = Math.floor(minutes / 60);
			return hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
		};
		const colour = (v: number) => (v >= 0 ? NEON_GREEN : NEON_RED);
		const stats: { label: string; value: string; sub: string; color?: string }[] = [
			{ label: 'Net Profit', value: money(overall.netProfit), sub: 'P&L (USD)', color: colour(overall.netProfit) },
			{ label: 'Win Rate', value: `${num(overall.winRate, 1)}%`, sub: '% of Trades' },
			{ label: 'Expectancy', value: money(overall.expectancy), sub: 'Per Trade', color: colour(overall.expectancy) },
			{ label: 'Max Drawdown', value: money(risk.maxDrawdown), sub: risk.maxDrawdownPercent === null ? 'of equity' : `${num(risk.maxDrawdownPercent, 1)}% of equity` },
			{ label: 'Profit Factor', value: num(overall.profitFactor), sub: 'Gross Win / Loss', color: overall.profitFactor >= 1 ? NEON_GREEN : NEON_RED },
			{ label: 'Sharpe Ratio', value: overall.sharpeRatio === null ? '—' : num(overall.sharpeRatio), sub: 'Annualised' },
			{ label: 'Avg RR', value: num(overall.avgRR), sub: 'Risk/Reward' },
			{ label: 'Avg Monthly Return', value: overall.avgMonthlyReturnPercent === null ? '—' : signPct(overall.avgMonthlyReturnPercent), sub: `${money(overall.avgMonthlyPnl)} / month`, color: overall.avgMonthlyReturnPercent === null ? undefined : colour(overall.avgMonthlyReturnPercent) },
			{ label: 'Max Win Streak', value: String(risk.maxWinStreak), sub: 'Consecutive Wins' },
			{ label: 'Max Loss Streak', value: String(risk.maxLossStreak), sub: 'Consecutive Losses' },
			{ label: 'Avg Time in Market', value: duration(insights.avgTradeTime), sub: 'Duration' },
			{ label: 'Skill Level', value: String(skill.level), sub: `Score ${Math.round(skill.score)}/100` },
			{ label: 'Plan Adherence', value: `${num(skill.planAdherence, 0)}%`, sub: 'Discipline' },
			{ label: 'Avg Confidence', value: `${num(skill.avgConfidence, 1)}/10`, sub: 'Self-Rating' },
		];
		d.qs<HTMLElement>('#rp-stats').innerHTML = stats.map(s => `
			<div class="rp-stat">
				<div class="rp-stat-label">${esc(s.label)}</div>
				<div class="rp-stat-value"${s.color ? ` style="color: ${s.color}"` : ''}>${esc(s.value)}</div>
				<div class="rp-stat-sub">${esc(s.sub)}</div>
			</div>
		`).join('');

		Report.renderCharts(equityCurve, timeBased.monthly, insights.bySymbol, timeBased.dayOfWeek);
		Report.fitToScreen();
	}

	private static renderCharts(equity: any[], monthly: any[], symbols: any[], days: any[]) {
		if (typeof Chart === 'undefined') { return; }
		Chart.defaults.font.family = "'Inter', 'Segoe UI', system-ui, sans-serif";
		Chart.defaults.color = TICK;
		const base = {
			responsive: true,
			maintainAspectRatio: false,
			animation: false,           // render fully before any capture
			devicePixelRatio: 2,        // crisp in the PDF
			plugins: { legend: { display: false }, tooltip: { enabled: false } },
		};
		const barColors = (vals: number[]) => vals.map(v => (v >= 0 ? NEON_GREEN : NEON_RED));

		// Equity with a cyan→purple glow line
		const eqCanvas = d.qs<HTMLCanvasElement>('#rp-equity-chart');
		const ctx = eqCanvas.getContext('2d')!;
		const fill = ctx.createLinearGradient(0, 0, 0, 240);
		fill.addColorStop(0, 'rgba(0, 229, 255, 0.35)');
		fill.addColorStop(1, 'rgba(168, 85, 247, 0.02)');
		new Chart(ctx, {
			type: 'line',
			data: {
				labels: equity.map((_, i) => i),
				datasets: [{ data: equity.map(p => p.balance), borderColor: '#e6f7ff', borderWidth: 1.8, backgroundColor: fill, fill: true, pointRadius: 0, tension: 0.15 }],
			},
			options: { ...base, scales: { x: { display: false }, y: { grid: { color: GRID }, ticks: { color: TICK, font: { size: 10 } } } } },
		});

		const monthlyVals = monthly.map(m => m.pnl);
		new Chart(d.qs<HTMLCanvasElement>('#rp-monthly-chart').getContext('2d'), {
			type: 'bar',
			data: { labels: monthly.map(m => m.month), datasets: [{ data: monthlyVals, backgroundColor: barColors(monthlyVals), borderRadius: 2 }] },
			options: { ...base, scales: { x: { grid: { display: false }, ticks: { color: TICK, font: { size: 10 } } }, y: { grid: { color: GRID }, ticks: { color: TICK, font: { size: 10 } } } } },
		});

		const top = symbols.slice(0, 6);
		const symVals = top.map(s => s.pnl);
		new Chart(d.qs<HTMLCanvasElement>('#rp-symbol-chart').getContext('2d'), {
			type: 'bar',
			data: { labels: top.map(s => s.symbol), datasets: [{ data: symVals, backgroundColor: barColors(symVals), borderRadius: 2 }] },
			options: { ...base, indexAxis: 'y', scales: { x: { grid: { color: GRID }, ticks: { color: TICK, font: { size: 9 } } }, y: { grid: { display: false }, ticks: { color: '#cfe3ff', font: { size: 10 } } } } },
		});

		const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
		const dayVals = dayNames.map(n => days.find(x => x.day === n)?.pnl || 0);
		new Chart(d.qs<HTMLCanvasElement>('#rp-day-chart').getContext('2d'), {
			type: 'bar',
			data: { labels: dayNames.map(n => n.slice(0, 3)), datasets: [{ data: dayVals, backgroundColor: barColors(dayVals), borderRadius: 2 }] },
			options: { ...base, scales: { x: { grid: { display: false }, ticks: { color: TICK, font: { size: 9 } } }, y: { grid: { color: GRID }, ticks: { color: TICK, font: { size: 9 } } } } },
		});
	}

	private static async download() {
		const btn = d.qs<HTMLButtonElement>('#report-download');
		const page = d.qs<HTMLElement>('#report-page');
		const original = btn.textContent;
		btn.disabled = true;
		btn.textContent = 'Building PDF…';

		const transform = page.style.transform;
		try {
			await Promise.all([
				loadScript('/tv/vendor/html2canvas.min.js'),
				loadScript('/tv/vendor/jspdf.umd.min.js'),
			]);
			page.style.transform = ''; // capture at true A4 size, not the on-screen scale
			const canvas = await (window as any).html2canvas(page, { scale: 2, backgroundColor: '#050a18', useCORS: true, logging: false });
			const { jsPDF } = (window as any).jspdf;
			const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
			pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, 210, 297);
			const name = `tradevision-report-${Report.params.accountNumber || 'account'}-${Report.params.startDate || 'all'}-to-${Report.params.endDate || 'now'}`.replace(/[^A-Za-z0-9._-]/g, '_');
			pdf.save(`${name}.pdf`);
		} catch (err) {
			console.error('PDF export failed', err);
			Toast.show('Could not build the PDF — try Print → Save as PDF instead.', 'error', 6000);
		} finally {
			page.style.transform = transform;
			btn.disabled = false;
			btn.textContent = original;
		}
	}
}
